import GridList, { GridListLayout } from "@client/content/controls/GridList/component";
import { getRootContainerProps } from "@client/content/lib/helper";
import { ModuleButton } from "@client/content/modules/Button";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import Rate from "@client/controls/Rate/component";
import date from "@client/lib/date";
import { joinDefined } from "@client/lib/misc";
import { type PageHasMoreResponse } from "@client/types";
import { useI18n } from "@common/hooks/i18n";
import classNames from "classnames";
import { isNull } from "lodash";
import * as React from "react";
import api from "../../api";
import { type ProductDetail } from "../../types";
import ReviewForm from "./Form/container";

export type ItemProps = ProductDetail.ProductReview;
const Item = (props: ItemProps) => (
  <div className={classNames("item", { declined: props.isApproved === false, pending: isNull(props.isApproved) })}>
    <div className="title">
      <Rate rate={props.rating} />
      {!props.isApproved && (
        <span className="badge">{props.isApproved === false ? "Declined" : "Pending Approval"}</span>
      )}
    </div>
    <div className="content">{props.review}</div>
    <div className="author">
      <span className="name">{joinDefined(" ", props.userFirstName, props.userLastName)}</span>
      <span className="date">{date.formatDate(props.createdOnDate)}</span>
    </div>
  </div>
);

export type MapProps = RootContainerProps & {
  addCaption: string;
  apiInfo: ModuleApiInfo;
  canReview: boolean;
  editCaption: string;

  product: ProductDetail.Product;
  reloadKey: string;
};

export interface ActionProps {}

export type Props = ActionProps & MapProps;

export default (props: Props) => {
  const i18n = useI18n();

  const isCanceleed = React.useRef(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [isFormVisible, setIsFormVisible] = React.useState(false);
  const [pageData, setPageData] = React.useState<PageHasMoreResponse<ProductDetail.Api.ProductReview>>({
    items: [],
    hasMore: false
  });

  React.useEffect(
    () => () => {
      isCanceleed.current = true;
    },
    []
  );

  React.useEffect(() => {
    load(1);
  }, [props.reloadKey]);

  React.useEffect(() => setIsFormVisible(false), [props.product.userReview]);

  const load = React.useCallback(
    async (page: number) => {
      setIsLoading(true);
      setPage(page);
      try {
        const res = await api.getReviews(props.apiInfo, props.product.id, {
          order: "desc",
          sortBy: "createdOnDate",
          page,
          size: 5
        });

        setPageData({
          items: res.response!.data,
          hasMore: res.response!.data.length === 5
        });
      } finally {
        !isCanceleed.current && setIsLoading(false);
      }
    },
    [props.apiInfo, props.product.id, setIsLoading, setPageData, setPage]
  );

  const onAddEditButtonClick = React.useCallback(() => setIsFormVisible(true), [setIsFormVisible]);
  return (
    <div className="reviews">
      {props.canReview && (
        <h4 className="title">
          {pageData.items.length ? i18n.reviews : props.product.allowReviews ? i18n.infoFirstReview : null}
          {!isFormVisible && props.product.allowReviews && (
            <ModuleButton {...getRootContainerProps(props)} onClick={onAddEditButtonClick}>
              {props.product.userReview ? props.editCaption : props.addCaption}
            </ModuleButton>
          )}
        </h4>
      )}

      {isFormVisible && props.canReview && <ReviewForm {...getRootContainerProps(props)} />}

      <GridList
        columns={1}
        gutter={20}
        layout={GridListLayout.Grid}
        isLoading={isLoading}
        ItemComponent={Item}
        items={pageData.items}
        load={load}
        page={page}
        pageSize={5}
        hasMore={pageData.hasMore}
      />
    </div>
  );
};
